import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Check, Zap, Star, Crown, ArrowRight, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GradientText } from '@/components/ui/GradientText';

// Animation variants
const cardVariants = {
  initial: { opacity: 0, y: 50, scale: 0.9 },
  animate: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: {
    y: -8,
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const featureVariants = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 }
};

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface PricingTier {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  agentLimit: number;
  isPopular?: boolean;
  isPremium?: boolean;
  features: string[];
  limits: {
    storage: string;
    apiCalls: string;
    support: string;
  };
  cta: string;
  savings?: string;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 1000,
    yearlyPrice: 10000,
    agentLimit: 2,
    isPopular: true, // Psychological effect - highlight entry tier
    features: [
      'Up to 2 AI Agents',
      'Basic analytics dashboard',
      'Email support',
      'Agent marketplace access',
      '5GB storage',
      'Standard API rate limits'
    ],
    limits: {
      storage: '5GB',
      apiCalls: '10K/month',
      support: 'Email only'
    },
    cta: 'Start Free Trial',
    savings: '₹2,000/year'
  },
  {
    id: 'professional',
    name: 'Professional',
    monthlyPrice: 5000,
    yearlyPrice: 50000,
    agentLimit: 5,
    features: [
      'Up to 5 AI Agents',
      'Advanced analytics & insights',
      'Priority email support',
      'Custom agent branding',
      'Agent revenue sharing',
      '25GB storage',
      'Higher API rate limits',
      'A/B testing tools',
      'Export capabilities'
    ],
    limits: {
      storage: '25GB',
      apiCalls: '50K/month',
      support: 'Priority email'
    },
    cta: 'Upgrade to Pro',
    savings: '₹10,000/year'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 15000,
    yearlyPrice: 150000,
    agentLimit: 999,
    isPremium: true,
    features: [
      'Unlimited AI Agents',
      'Real-time analytics & reporting',
      '24/7 phone & chat support',
      'White-label solutions',
      'Custom integrations',
      'Unlimited storage',
      'Dedicated API limits',
      'Advanced workflow automation',
      'Custom training',
      'Dedicated account manager',
      'SLA guarantee',
      'Custom contracts'
    ],
    limits: {
      storage: 'Unlimited',
      apiCalls: 'Unlimited',
      support: '24/7 phone + chat'
    },
    cta: 'Contact Sales',
    savings: '₹30,000/year'
  }
];

export function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();

  const handleSubscribe = (tierId: string) => {
    // Track conversion event
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'INR',
        value: isYearly 
          ? pricingTiers.find(t => t.id === tierId)?.yearlyPrice 
          : pricingTiers.find(t => t.id === tierId)?.monthlyPrice,
        items: [{
          item_id: tierId,
          item_name: pricingTiers.find(t => t.id === tierId)?.name,
          category: 'Subscription'
        }]
      });
    }
    
    // Navigate to subscription page instead of redirect
    navigate(`/subscribe/${tierId}?billing=${isYearly ? 'yearly' : 'monthly'}`);
  };

  const handleContactSales = () => {
    // For enterprise tier, redirect to contact/booking page
    navigate('/contact');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-background to-muted/20 dark:from-background dark:to-muted/10" id="pricing">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-heading font-bold mb-4 text-foreground">
            Choose Your{" "}
            <span className="text-gradient-brand">
              Growth Plan
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Start building intelligent agents today. Scale your business with our flexible pricing options.
          </p>
          
          {/* Billing Toggle with Animation */}
          <motion.div 
            className="flex items-center justify-center gap-4 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <span className={`text-sm font-medium transition-colors ${
              !isYearly ? 'text-brand-purple' : 'text-muted-foreground'
            }`}>
              Monthly
            </span>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Switch
                checked={isYearly}
                onCheckedChange={setIsYearly}
                className="data-[state=checked]:bg-brand-purple"
              />
            </motion.div>
            <span className={`text-sm font-medium transition-colors ${
              isYearly ? 'text-brand-purple' : 'text-muted-foreground'
            }`}>
              Yearly
            </span>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <Badge variant="secondary" className="bg-brand-orange/10 text-brand-orange border-brand-orange/20 ml-2">
                Save up to 17%
              </Badge>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Pricing Cards with Stagger Animation */}
        <motion.div 
          className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          {pricingTiers.map((tier, index) => {
            const monthlyPrice = isYearly ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice;
            
            return (
              <motion.div
                key={tier.id}
                variants={cardVariants}
                initial="initial"
                whileInView="animate"
                whileHover="hover"
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="h-full"
              >
                <Card
                  className={`relative h-full transition-all duration-500 hover:shadow-2xl border group card-brand ${
                    tier.isPopular
                      ? 'ring-2 ring-brand-purple scale-105 z-10 shadow-brand-lg bg-gradient-to-b from-brand-purple/5 to-background border-brand-purple/20'
                      : tier.isPremium
                      ? 'ring-1 ring-brand-orange/30 bg-gradient-to-b from-brand-orange/5 to-background hover:ring-brand-orange/50'
                      : 'hover:shadow-brand border-border hover:border-brand-purple/30'
                  }`}
                >
                  {/* Animated glow effect */}
                  <motion.div
                    className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                      tier.isPopular 
                        ? 'bg-gradient-to-r from-brand-purple/10 to-brand-orange/10' 
                        : tier.isPremium
                        ? 'bg-gradient-to-r from-brand-orange/10 to-brand-purple/10'
                        : 'bg-gradient-to-r from-brand-purple/5 to-brand-orange/5'
                    }`}
                    style={{ filter: 'blur(20px)', zIndex: -1 }}
                  />

                  {/* Popular Badge with Animation */}
                  {tier.isPopular && (
                    <motion.div 
                      className="absolute -top-4 left-1/2 -translate-x-1/2 z-20"
                      initial={{ opacity: 0, y: -10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <Badge className="bg-brand-purple text-white px-4 py-1 rounded-full text-sm font-semibold shadow-brand">
                        <motion.div
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        >
                          <Star className="w-4 h-4 mr-1 fill-current" />
                        </motion.div>
                        Most Popular
                      </Badge>
                    </motion.div>
                  )}
                  
                  {/* Premium Badge with Animation */}
                  {tier.isPremium && (
                    <motion.div 
                      className="absolute -top-4 left-1/2 -translate-x-1/2 z-20"
                      initial={{ opacity: 0, y: -10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <Badge className="bg-gradient-brand text-white px-4 py-1 rounded-full text-sm font-semibold shadow-brand">
                        <motion.div
                          animate={{ 
                            rotate: [0, 5, -5, 0],
                            scale: [1, 1.1, 1]
                          }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <Crown className="w-4 h-4 mr-1" />
                        </motion.div>
                        Enterprise
                      </Badge>
                    </motion.div>
                  )}

                  <CardHeader className="text-center pb-8">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2 text-foreground">
                        {tier.name}
                        {tier.isPopular && (
                          <motion.div
                            animate={{ 
                              scale: [1, 1.2, 1],
                              color: ["#eab308", "#f59e0b", "#eab308"]
                            }}
                            transition={{ 
                              duration: 2, 
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <Zap className="w-5 h-5" />
                          </motion.div>
                        )}
                      </CardTitle>
                    </motion.div>
                    
                    <motion.div 
                      className="mt-4"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex items-baseline justify-center gap-1">
                        <motion.span 
                          className="text-4xl font-bold text-foreground"
                          key={`${tier.id}-${isYearly}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          ₹{monthlyPrice.toLocaleString()}
                        </motion.span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      
                      {isYearly && tier.savings && (
                        <motion.div 
                          className="mt-2"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                        >
                          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 animate-pulse">
                            Save {tier.savings}
                          </Badge>
                        </motion.div>
                      )}
                      
                      <p className="text-sm text-muted-foreground mt-2">
                        {tier.agentLimit === 999 ? 'Unlimited' : `Up to ${tier.agentLimit}`} AI Agents
                      </p>

                      {/* Purchase Button - Moved right after price */}
                      <div className="mt-6">
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                          <Button
                            className={`w-full h-12 font-heading font-semibold text-base transition-all relative overflow-hidden group/button ${
                              tier.isPopular
                                ? 'btn-brand-primary shadow-brand-lg'
                                : tier.isPremium
                                ? 'btn-brand-gradient shadow-brand-lg'
                                : 'btn-brand-outline'
                            }`}
                            size="lg"
                            onClick={() => tier.id === 'enterprise' ? handleContactSales() : handleSubscribe(tier.id)}
                          >
                            {/* Button glow effect */}
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/button:opacity-100 transition-opacity duration-300"
                              initial={{ x: "-100%" }}
                              whileHover={{ x: "100%" }}
                              transition={{ duration: 0.6 }}
                            />
                            <span className="relative z-10">{tier.cta}</span>
                            <motion.div
                              className="relative z-10 ml-2"
                              whileHover={{ x: 3 }}
                              transition={{ type: "spring", stiffness: 400 }}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </motion.div>
                          </Button>
                        </motion.div>
                        
                        {tier.id === 'starter' && (
                          <motion.p 
                            className="text-xs text-center text-muted-foreground mt-2"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            viewport={{ once: true }}
                          >
                            7-day free trial • No credit card required
                          </motion.p>
                        )}
                        
                        {tier.id === 'enterprise' && (
                          <motion.div 
                            className="mt-3 space-y-2"
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            viewport={{ once: true }}
                          >
                            <p className="text-xs text-center text-muted-foreground">
                              Custom pricing available • Volume discounts
                            </p>
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-10 text-sm hover:bg-brand-purple/10 hover:border-brand-purple/50 focus-brand"
                                onClick={handleContactSales}
                              >
                                <Phone className="w-4 h-4 mr-2" />
                                Book a Meeting
                              </Button>
                            </motion.div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Features with Stagger Animation */}
                    <div className="space-y-3">
                      {tier.features.map((feature, featureIndex) => (
                        <motion.div 
                          key={featureIndex} 
                          className="flex items-start gap-3 group/feature"
                          variants={featureVariants}
                          initial="initial"
                          whileInView="animate"
                          transition={{ delay: featureIndex * 0.05 }}
                          viewport={{ once: true }}
                          whileHover={{ x: 5 }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 360 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                          </motion.div>
                          <span className="text-sm text-foreground group-hover/feature:text-primary transition-colors duration-200">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Limits */}
                    <div className="border-t border-border pt-4">
                      <h4 className="font-semibold mb-3 text-sm text-foreground">Plan Includes:</h4>
                      <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground">
                        <motion.div whileHover={{ scale: 1.02, x: 2 }}>📁 Storage: {tier.limits.storage}</motion.div>
                        <motion.div whileHover={{ scale: 1.02, x: 2 }}>🔗 API Calls: {tier.limits.apiCalls}</motion.div>
                        <motion.div whileHover={{ scale: 1.02, x: 2 }}>💬 Support: {tier.limits.support}</motion.div>
                      </div>
                    </div>

                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA with Animation */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-muted-foreground mb-4">
            Need a custom solution? We've got you covered.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button variant="outline" size="lg" className="hover:bg-brand-purple/10 hover:border-brand-purple/50 focus-brand">
              Schedule a Demo
            </Button>
          </motion.div>
        </motion.div>

        {/* Trust Indicators with Animation */}
        <motion.div 
          className="mt-16 pt-8 border-t"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: "✅", title: "No Setup Fees", desc: "Get started immediately with no hidden costs" },
              { icon: "🔒", title: "Secure & Compliant", desc: "Enterprise-grade security and data protection" },
              { icon: "📞", title: "Expert Support", desc: "Get help from our AI specialists when you need it" }
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="group cursor-default"
              >
                <motion.h4 
                  className="font-semibold mb-2 group-hover:text-primary transition-colors duration-300"
                  whileHover={{ scale: 1.1 }}
                >
                  <motion.span
                    className="inline-block mr-2"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {item.icon}
                  </motion.span>
                  {item.title}
                </motion.h4>
                <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
